import { useCallback } from 'react';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';
import { useToast, Button } from '@app/core';
import {
  ConsentName,
  useConsentUrl,
  useIsParticipant,
  useMarkAsNotInterested,
} from '@app/api';
import { useNavigate } from 'react-router-dom';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginTop: theme.marginBase * 3,
  },
  legalInfo: {
    ...theme.fonts.calendarText,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    border: {
      style: 'solid',
      width: 1,
      color: theme.colors.grayBorder,
    },
    color: theme.colors.inkSecondary,
  },
  link: {
    color: theme.colors.blueAccessible,
  },
  buttonContainer: {
    marginTop: theme.marginBase * 2,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: theme.marginBase,
    [theme.media.mobile]: {
      flexDirection: 'column',
      marginTop: theme.marginBase * 4,
    },
  },
}));

interface Props {
  participationId?: string | undefined;
  onClick: () => any;
  buttonIsLoading?: boolean;
  buttonIsDisabled?: boolean;
}

export const ParticipateButton = ({
  onClick,
  buttonIsLoading,
  buttonIsDisabled,
  participationId,
}: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const isParticipant = useIsParticipant();

  const consentUrl = useConsentUrl(ConsentName.PARTICIPANT_PRIVACY);
  const { mutateAsync: markAsNotInterested } = useMarkAsNotInterested(
    participationId || '',
  );

  const isParticipantAndHaveParticipation = isParticipant && participationId;

  const onNotInterestedClick = useCallback(async () => {
    try {
      await markAsNotInterested();
      navigate(`/p/${participationId}`);
    } catch (e) {
      toast.exception(e);
    }
  }, [navigate, toast, markAsNotInterested]);

  return (
    <div className={classes.container}>
      <div className={classes.legalInfo}>
        <Trans
          i18nKey="application.info.legal"
          components={{
            a: (
              <a
                href={consentUrl}
                className={classes.link}
                target="_blank"
                rel="noreferrer noopener"
              />
            ),
          }}
        />
      </div>

      <div className={classes.buttonContainer}>
        <Button
          onClick={onClick}
          text={t('application.submit')}
          fullMobileWidth
          isLoading={buttonIsLoading}
          disabled={buttonIsDisabled}
          trackingName="Start Applying Button"
        />
        {isParticipantAndHaveParticipation && (
          <Button
            color="red"
            variant="transparent"
            text={t('home.applyStudies.btnNotInterested')}
            fullMobileWidth
            onClick={onNotInterestedClick}
          />
        )}
      </div>
    </div>
  );
};
